import config from "./config";
import Cookies from "js-cookie";
import { useMutation, useQuery } from "@tanstack/react-query";
import { InvestorDto, PartialInvestorDto } from "./dtos/investor.dto";
import { InvestmentProfileDto } from "./dtos/investment-profile.dto";
import { ProfileStatusDto } from "./dtos/profile-status.dto";
import { SettingsDto } from "./dtos/settings.dto";
import { ResponseError, ResponseErrorBody } from "./types/responseError";
import { ProfileConfigDto } from "./dtos/profile-config.dto";
import { DocumentsInfoDto } from "./dtos/documents-info.dto";
import { UploadTempDocumentResponseDto } from "./dtos/upload-temp-document.dto";
import {
  UploadInvestorDocumentsPayload,
  UploadInvestorDocumentsResponse,
} from "./dtos/upload-investor-documents.dto";
import { GetKeyPartiesResponseDto } from "./dtos/get-key-parties.dto";
import { deleteKeyParty, updateInvestor, updateKeyParty } from "./api";
import { KeyParty } from "./dtos/get-key-party.dto";
import { GetRequiredKeyPartyDocumentsResponseDto } from "./dtos/get-required-key-party-documents.dto";
import { GetKeyPartyDocumentsResponseDto } from "./dtos/key-party-documents.dto";

export const useGetInvestorInformationQuery = ({
  enabled,
}: {
  enabled?: boolean;
} = {}) =>
  useQuery<InvestorDto, ResponseError>({
    queryKey: ["investor-information"],
    queryFn: async () => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/profile/information`,
        {
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
        },
      );
      if (!response.ok) {
        console.error(await response.text());
        throw new ResponseError(
          "Error fetching investor information",
          response,
        );
      }

      return response.json() as Promise<InvestorDto>;
    },
    enabled,
  });

export const useGetInvestmentProfileQuery = ({
  enabled,
}: { enabled?: boolean } = {}) =>
  useQuery<InvestmentProfileDto, ResponseError>({
    queryKey: ["investment-profile"],
    queryFn: async () => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/profile/investment-profile`,
        {
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
        },
      );
      if (!response.ok) {
        console.error(await response.text());
        throw new ResponseError("Error fetching investment profile", response);
      }

      return response.json() as Promise<InvestmentProfileDto>;
    },
    enabled,
  });

export const useGetDocumentsQuery = ({ enabled }: { enabled?: boolean } = {}) =>
  useQuery<DocumentsInfoDto, ResponseError>({
    queryKey: ["documents"],
    queryFn: async () => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/profile/documents`,
        {
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
        },
      );
      if (!response.ok) {
        console.error(await response.text());
        throw new ResponseError("Error fetching documents tab info", response);
      }

      return response.json() as Promise<DocumentsInfoDto>;
    },
    enabled,
  });

export const useGetSettingsQuery = ({ enabled }: { enabled?: boolean } = {}) =>
  useQuery<SettingsDto, ResponseError>({
    queryKey: ["settings"],
    queryFn: async () => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/profile/settings`,
        {
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
        },
      );
      if (!response.ok) {
        console.error(await response.text());
        throw new ResponseError("Error fetching settings", response);
      }

      return response.json() as Promise<SettingsDto>;
    },
    enabled,
  });

export const useGetPendingActionsQuery = () =>
  useQuery({
    queryKey: ["pending-actions"],
    queryFn: async () => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/profile/pending-actions`,
        {
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
        },
      );
      if (!response.ok) {
        console.error(await response.text());
        throw new Error("Error fetching pending actions");
      }

      return response.json() as Promise<ProfileStatusDto>;
    },
  });

export const useGetConfigQuery = () =>
  useQuery<ProfileConfigDto, ResponseError>({
    queryKey: ["profile-config"],
    queryFn: async () => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/profile/config`,
        {
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
        },
      );
      if (!response.ok) {
        console.error(await response.text());
        throw new ResponseError("Error fetching profile config", response);
      }

      return response.json() as Promise<ProfileConfigDto>;
    },
  });

export const useDownloadInfoMutation = () =>
  useMutation<void, ResponseError>({
    mutationFn: async () => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/profile/request-data`,
        {
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
          method: "POST",
        },
      );
      if (!response.ok) {
        console.error(await response.text());
        throw new ResponseError("Error requesting data download", response);
      }
    },
  });

export const useDeleteInvestorMutation = () =>
  useMutation<{ accountDisabled: boolean }, ResponseError>({
    mutationFn: async () => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/investor/request-deletion`,
        {
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
          method: "POST",
        },
      );
      if (!response.ok) {
        console.error(await response.text());
        throw new ResponseError("Error requesting data download", response);
      }

      return response.json();
    },
  });

export const useRemoveAccessMutation = () =>
  useMutation<void, ResponseError>({
    mutationFn: async () => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/investor/remove-user-access`,
        {
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
          method: "POST",
        },
      );
      if (!response.ok) {
        console.error(await response.text());
        throw new ResponseError("Error requesting data download", response);
      }
    },
  });

export const useUpdateInvestorMutation = () =>
  useMutation<void, ResponseError, PartialInvestorDto>({
    mutationFn: async (partialInvestorDto: PartialInvestorDto) =>
      updateInvestor(partialInvestorDto),
  });

export const useUploadInvestorDocumentTempMutation = () =>
  useMutation<UploadTempDocumentResponseDto, ResponseError, { file: File }>({
    mutationFn: async ({ file }) => {
      const formData = new FormData();
      formData.append("file", file);

      const url = `${config.SID_GW_BASE_URL}/v1/investor/documents/temp`;

      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
          Authorization: Cookies.get("auth-token") ?? "",
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        const errorBody = JSON.parse(errorText) as ResponseErrorBody;
        console.error("Error uploading temporary document:", errorText);
        throw new ResponseError(
          "Error uploading temporary document",
          response,
          errorBody,
        );
      }

      return response.json() as Promise<UploadTempDocumentResponseDto>;
    },
  });

export const useUploadInvestorDocumentsMutation = () => {
  return useMutation<
    UploadInvestorDocumentsResponse,
    ResponseError,
    UploadInvestorDocumentsPayload
  >({
    mutationFn: async (payload) => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/investor/documents`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error uploading investor documents:", errorText);
        throw new ResponseError("Error uploading investor documents", response);
      }

      return response.json() as Promise<UploadInvestorDocumentsResponse>;
    },
  });
};

export const useDeleteInvestorDocumentMutation = () => {
  return useMutation<void, ResponseError, { documentId: string }>({
    mutationFn: async ({ documentId }) => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/investor/documents/${documentId}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error deleting document:", errorText);
        throw new ResponseError("Error deleting document", response);
      }
    },
  });
};

export const useGetArchiveMutation = () =>
  useMutation<Blob, Error | ResponseError>({
    mutationFn: async () => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/profile/archive`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token")!,
          },
        },
      );
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error getting URL:", errorText);
        throw new ResponseError("Error downloading your data", response);
      }
      const { url } = await (response.json() as Promise<{
        url: string;
      }>);

      const data = await fetch(url);
      if (!data.ok) {
        const errorText = await data.text();
        console.error("Error downloading archive:", errorText);
        throw new Error("Error downloading archive");
      }

      return data.blob();
    },
  });

export const useSubmitVerification = () =>
  useMutation<void, ResponseError>({
    mutationFn: async () => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/profile/verification/submit`,
        {
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
          method: "POST",
        },
      );
      if (!response.ok) {
        console.error(await response.text());
        throw new ResponseError("Error submitting verification", response);
      }
    },
  });

export const useGetKeyPartiesQuery = ({
  enabled,
}: { enabled?: boolean } = {}) =>
  useQuery<GetKeyPartiesResponseDto, ResponseError>({
    queryKey: ["key-parties"],
    queryFn: async () => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/profile/key-parties`,
        {
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
        },
      );
      if (!response.ok) {
        console.error(await response.text());
        throw new ResponseError("Error fetching key parties", response);
      }

      return response.json();
    },
    enabled,
  });

export const useDeleteKeyPartyMutation = () =>
  useMutation<void, ResponseError, { keyPartyId: string }>({
    mutationFn: ({ keyPartyId }: { keyPartyId: string }) =>
      deleteKeyParty(keyPartyId),
  });

export const useUpdateKeyPartyMutation = () =>
  useMutation<
    KeyParty,
    ResponseError,
    { keyPartyId: string; keyPartyData: Partial<KeyParty> }
  >({
    mutationFn: ({ keyPartyId, keyPartyData }) =>
      updateKeyParty(keyPartyId, keyPartyData),
  });

export const useRequiredKeyPartyDocumentsQuery = (
  keyPartyType: string,
  entityType?: string,
) =>
  useQuery({
    queryKey: ["key-party-required-documents", keyPartyType, entityType],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append("keyPartyType", keyPartyType);
      if (entityType) {
        params.append("keyPartyEntityType", entityType);
      }
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/investor/required-key-party-documents?${params.toString()}`,
        {
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
        },
      );
      if (!response.ok) {
        console.error(await response.text());
        throw new ResponseError(
          "Error fetching required key party documents",
          response,
        );
      }

      return response.json() as Promise<GetRequiredKeyPartyDocumentsResponseDto>;
    },
  });

export const useGetKeyPartyDocumentsQuery = (keyPartyId: string | undefined) =>
  useQuery({
    queryKey: ["key-party-documents", keyPartyId],
    queryFn: async () => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/profile/key-parties/${keyPartyId ?? ""}/documents`,
        {
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
        },
      );
      if (!response.ok) {
        console.error(await response.text());
        throw new ResponseError("Error fetching key party documents", response);
      }

      return response.json() as Promise<GetKeyPartyDocumentsResponseDto>;
    },
    enabled: !!keyPartyId,
  });

export const useAddKeyPartyDocumentMutation = () =>
  useMutation({
    mutationFn: async ({
      keyPartyId,
      documentType,
      documentCategory,
      fileName,
      fileType,
      side,
    }: {
      keyPartyId: string;
      documentType: string;
      documentCategory: string;
      fileName: string;
      fileType: string;
      side: string;
    }) => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/profile/key-parties/${keyPartyId}/documents`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            documentType,
            documentCategory,
            fileName,
            fileType,
            side,
          }),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error adding key party document:", errorText);
        throw new ResponseError("Error adding key party document", response);
      }

      return response.json();
    },
  });

export const useDeleteKeyPartyDocumentMutation = () =>
  useMutation<
    void,
    ResponseError,
    {
      keyPartyId: string;
      documentId: string;
    }
  >({
    mutationFn: async ({ keyPartyId, documentId }) => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/profile/key-parties/${keyPartyId}/documents/${encodeURIComponent(documentId)}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error deleting key party document:", errorText);
        throw new ResponseError("Error deleting key party document", response);
      }
    },
  });
